import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  Cookie as CookieIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon,
  Delete as DeleteIcon,
  Update as UpdateIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const sections = [
    {
      id: 'introduction',
      title: '1. Introdução',
      icon: InfoIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Bem-vindo ao <strong>Hostly</strong>! A sua privacidade é extremamente importante para nós.
            Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas
            informações pessoais quando você utiliza nosso aplicativo.
          </Typography>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            O Hostly é um aplicativo de organização de eventos sociais que permite aos usuários gerenciar
            convidados, receitas, acervo de louças, listas de tarefas e muito mais.
          </Typography>
          <Typography variant="body1">
            Ao utilizar o Hostly, você concorda com a coleta e uso de informações de acordo com esta política.
            Esta Política está em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong>.
          </Typography>
        </>
      ),
    },
    {
      id: 'data-collection',
      title: '2. Informações que Coletamos',
      icon: AccountCircleIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Coletamos diferentes tipos de informações para fornecer e melhorar nosso serviço:
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
            2.1. Informações Fornecidas por Você
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Dados de Cadastro"
                secondary="Nome, sobrenome, e-mail, telefone, data de nascimento, gênero e foto de perfil"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Conteúdo Criado"
                secondary="Eventos, lista de convidados, receitas, acervo de louças, listas de tarefas e compras, wishlist"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Mídia"
                secondary="Fotos e vídeos que você carrega no aplicativo"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Dados de Convidados"
                secondary="Informações de contatos que você adiciona (nome, telefone, e-mail, endereço, preferências alimentares)"
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            2.2. Informações Coletadas Automaticamente
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Dados de Uso"
                secondary="Como você interage com o aplicativo, recursos utilizados, frequência de acesso"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Dados do Dispositivo"
                secondary="Tipo de dispositivo, sistema operacional, identificador único do dispositivo"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Dados de Localização"
                secondary="Localização aproximada para funcionalidades específicas (com seu consentimento)"
              />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'data-usage',
      title: '3. Como Usamos Suas Informações',
      icon: SecurityIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Utilizamos as informações coletadas para as seguintes finalidades:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Fornecer, operar e manter o aplicativo Hostly" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Processar e gerenciar sua conta de usuário" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Permitir que você crie e gerencie eventos, convidados, receitas e acervo" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Sincronizar seus dados com o Google Agenda (apenas plano Premium/Pro)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Fornecer o Assistente com IA para sugestões personalizadas (apenas plano Pro)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Enviar notificações sobre eventos, lembretes e atualizações do aplicativo" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Melhorar a experiência do usuário e desenvolver novos recursos" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Prevenir fraudes e garantir a segurança da plataforma" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Cumprir obrigações legais e regulatórias" />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'data-sharing',
      title: '4. Compartilhamento de Informações',
      icon: ShareIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            <strong>Nós NÃO vendemos suas informações pessoais.</strong> Podemos compartilhar suas informações apenas nas seguintes circunstâncias:
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
            4.1. Prestadores de Serviço
          </Typography>
          <Typography variant="body2" sx={{
            marginBottom: "16px"
          }}>
            Compartilhamos dados com terceiros que nos ajudam a operar o aplicativo:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Firebase (Google)"
                secondary="Hospedagem, autenticação, banco de dados e armazenamento"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Google Calendar API"
                secondary="Sincronização de eventos (apenas com sua autorização)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Provedores de IA"
                secondary="Processamento de sugestões inteligentes (plano Pro)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Processadores de Pagamento"
                secondary="Para gerenciar assinaturas Premium e Pro"
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            4.2. Requisitos Legais
          </Typography>
          <Typography variant="body2" sx={{
            marginBottom: "16px"
          }}>
            Podemos divulgar suas informações se formos obrigados por lei ou para:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Cumprir uma obrigação legal" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Proteger os direitos ou propriedade do Hostly" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Prevenir ou investigar possíveis irregularidades" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Proteger a segurança dos usuários ou do público" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            4.3. HostlyClub (Rede Social Interna)
          </Typography>
          <Typography variant="body2">
            Ao participar do HostlyClub, você pode optar por compartilhar receitas, fotos de eventos e
            interagir com outros usuários. Essas informações serão visíveis para outros membros da comunidade
            conforme suas configurações de privacidade.
          </Typography>
        </>
      ),
    },
    {
      id: 'data-storage',
      title: '5. Armazenamento e Segurança',
      icon: LockIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Levamos a segurança dos seus dados muito a sério:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Criptografia"
                secondary="Todos os dados são criptografados em trânsito (HTTPS/TLS) e em repouso"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Autenticação"
                secondary="Senhas são criptografadas usando Firebase Authentication"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Servidores Seguros"
                secondary="Dados armazenados em servidores seguros do Google Cloud (Firebase)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Backup Automático"
                secondary="Backups regulares para planos Premium e Pro"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Acesso Restrito"
                secondary="Apenas pessoal autorizado tem acesso aos sistemas"
              />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: theme.colors.text.secondary }}>
            <strong>Importante:</strong> Embora implementemos medidas de segurança rigorosas, nenhum método de
            transmissão ou armazenamento eletrônico é 100% seguro. Não podemos garantir segurança absoluta.
          </Typography>
        </>
      ),
    },
    {
      id: 'user-rights',
      title: '6. Seus Direitos (LGPD)',
      icon: AccountCircleIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            De acordo com a LGPD, você tem os seguintes direitos sobre seus dados pessoais:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Acesso"
                secondary="Solicitar cópia de todos os dados que temos sobre você"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Correção"
                secondary="Atualizar ou corrigir dados incorretos ou incompletos"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Exclusão"
                secondary="Solicitar a exclusão de seus dados (direito ao esquecimento)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Portabilidade"
                secondary="Exportar seus dados em formato legível (PDF/CSV)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Revogação de Consentimento"
                secondary="Retirar seu consentimento para processamento de dados a qualquer momento"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Oposição"
                secondary="Opor-se ao processamento de dados em determinadas situações"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Informação"
                secondary="Ser informado sobre como seus dados são usados"
              />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Para exercer qualquer um desses direitos, entre em contato conosco através do e-mail:
            <strong> privacidade@hostly.app</strong>
          </Typography>
        </>
      ),
    },
    {
      id: 'cookies',
      title: '7. Cookies e Tecnologias Similares',
      icon: CookieIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Utilizamos cookies e tecnologias similares para melhorar sua experiência:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Cookies Essenciais"
                secondary="Necessários para o funcionamento básico do aplicativo (login, sessão)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Cookies de Preferência"
                secondary="Armazenam suas preferências (tema escuro/claro, idioma)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText
                primary="Cookies Analíticos"
                secondary="Nos ajudam a entender como você usa o aplicativo para melhorá-lo"
              />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Você pode gerenciar suas preferências de cookies nas configurações do seu navegador.
            Observe que desabilitar alguns cookies pode afetar a funcionalidade do aplicativo.
          </Typography>
        </>
      ),
    },
    {
      id: 'children',
      title: '8. Dados de Menores de Idade',
      icon: SecurityIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            O Hostly não é destinado a menores de 18 anos. Não coletamos intencionalmente informações
            pessoais de crianças ou adolescentes.
          </Typography>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Se você acredita que coletamos inadvertidamente dados de um menor de idade, entre em contato
            conosco imediatamente para que possamos tomar as medidas apropriadas.
          </Typography>
        </>
      ),
    },
    {
      id: 'retention',
      title: '9. Retenção de Dados',
      icon: DeleteIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Mantemos suas informações pessoais apenas pelo tempo necessário para:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Fornecer nossos serviços" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Cumprir obrigações legais" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Resolver disputas" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText primary="Fazer cumprir nossos acordos" />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Quando você solicita a exclusão de sua conta, excluímos permanentemente seus dados dentro de
            30 dias, exceto quando somos obrigados por lei a retê-los.
          </Typography>
        </>
      ),
    },
    {
      id: 'changes',
      title: '10. Alterações nesta Política',
      icon: UpdateIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre
            quaisquer alterações publicando a nova Política de Privacidade nesta página e atualizando
            a data de "Última Atualização".
          </Typography>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Recomendamos que você revise esta Política periodicamente para se manter informado sobre
            como protegemos suas informações.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Alterações significativas serão comunicadas por e-mail ou notificação no aplicativo.
          </Typography>
        </>
      ),
    },
    {
      id: 'contact',
      title: '11. Entre em Contato',
      icon: EmailIcon,
      content: (
        <>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade
            ou ao tratamento de seus dados pessoais, entre em contato conosco:
          </Typography>
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#F5F5F5', borderRadius: theme.radii.medium }}>
            <Typography variant="body2" sx={{
              marginBottom: "16px"
            }}>
              <strong>Hostly - Organização de Eventos</strong>
            </Typography>
            <Typography variant="body2" sx={{
              marginBottom: "16px"
            }}>
              <strong>E-mail:</strong> privacidade@hostly.app
            </Typography>
            <Typography variant="body2" sx={{
              marginBottom: "16px"
            }}>
              <strong>E-mail de Suporte:</strong> suporte@hostly.app
            </Typography>
            <Typography variant="body2" sx={{
              marginBottom: "16px"
            }}>
              <strong>Encarregado de Dados (DPO):</strong> dpo@hostly.app
            </Typography>
            <Typography variant="body2">
              <strong>Endereço:</strong> Brasília-DF, Brasil
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Responderemos sua solicitação dentro de 15 dias úteis, conforme estabelecido pela LGPD.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      pb: 4,
    }}>
      {/* Header fixo com logo */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: theme.colors.primary,
        borderBottomLeftRadius: 28,
        borderBottomRightRadius: 28,
        boxShadow: 3,
        overflow: 'hidden',
      }}>
        {/* Flores decorativas */}
        <img
          src="/hostly-02.png"
          alt="Logo decorativa"
          style={{
            position: "absolute",
            top: -20,
            left: -60,
            width: 180,
            opacity: 0.3,
            transform: "rotate(15deg)"
          }}
        />

        <img
          src="/hostly-02.png"
          alt="Logo decorativa"
          style={{
            position: "absolute",
            bottom: -10,
            right: -60,
            width: 180,
            opacity: 0.2
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            py: 3,
            gap: 2,
            position: 'relative',
            zIndex: 2,
          }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Logo central */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <img
                src="/hostly_Prancheta 1.png"
                alt="Hostly Logo"
                style={{
                  height: "60px",
                  width: "auto",
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Política de Privacidade
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Última atualização: 29 de setembro de 2025
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Conteúdo */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Resumo em destaque */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: theme.colors.secondary,
            color: 'white',
            borderRadius: theme.radii.medium,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SecurityIcon sx={{ fontSize: 40 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Seu Compromisso com a Privacidade
            </Typography>
          </Box>
          <Typography variant="body1" sx={{
            marginBottom: "16px"
          }}>
            No Hostly, respeitamos sua privacidade e estamos comprometidos em proteger seus dados pessoais
            em conformidade com a LGPD (Lei Geral de Proteção de Dados).
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="LGPD Compliant" sx={{ backgroundColor: 'white', fontWeight: 600 }} />
            <Chip label="Dados Criptografados" sx={{ backgroundColor: 'white', fontWeight: 600 }} />
            <Chip label="Você Controla Seus Dados" sx={{ backgroundColor: 'white', fontWeight: 600 }} />
          </Box>
        </Paper>

        {/* Accordion de seções */}
        {sections.map((section) => (
          <Accordion
            key={section.id}
            defaultExpanded={section.id === 'introduction'}
            sx={{
              mb: 2,
              borderRadius: theme.radii.medium,
              '&:before': {
                display: 'none',
              },
              boxShadow: 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#FAFAFA',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <section.icon sx={{ color: theme.colors.primary, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {section.content}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Footer */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 4,
            backgroundColor: '#F5F5F5',
            borderRadius: theme.radii.medium,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
            Esta Política de Privacidade foi atualizada pela última vez em 29 de setembro de 2025.
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 1 }}>
            © 2025 Hostly. Todos os direitos reservados.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;